import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Layout as LayoutIcon, GripVertical, RotateCcw, Save, Loader2, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * /admin/layouts — drag-and-drop builder for the public venue page (sections)
 * and the owner dashboard (modules). Supports a global default per plan +
 * per-venue overrides. Resolution order on read: venue override → plan
 * default → built-in catalogue.
 */
export default function AdminLayouts() {
  const { t } = useTranslation();
  const [scope, setScope] = useState("global"); // "global" | "venue"
  const [layoutType, setLayoutType] = useState("public"); // "public" | "owner"
  const [planKey, setPlanKey] = useState("free_listing");
  const [venueId, setVenueId] = useState("");
  const [plans, setPlans] = useState([]);
  const [venues, setVenues] = useState([]);
  const [catalogue, setCatalogue] = useState({ public_sections: [], owner_modules: [] });
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({}); // {has_override, resolved_source, is_default}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load plans, venues, and the section catalogue on mount.
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/venue-plans`),
      axios.get(`${API}/admin/venues?limit=500&status=all`),
      axios.get(`${API}/layouts/catalogue`),
    ]).then(([pl, vs, cat]) => {
      setPlans(pl.data || []);
      setVenues(vs.data?.venues || []);
      setCatalogue(cat.data || {});
      if (vs.data?.venues?.length) setVenueId(vs.data.venues[0].venue_id);
    }).finally(() => setLoading(false));
  }, []);

  // Reload the editor whenever scope/type/plan/venue changes.
  useEffect(() => {
    if (loading) return;
    let url, isVenueScope = scope === "venue";
    if (isVenueScope) {
      if (!venueId) return;
      url = `${API}/admin/venues/${venueId}/layout/${layoutType}`;
    } else {
      url = `${API}/admin/layouts/global/${planKey}/${layoutType}`;
    }
    axios.get(url).then((r) => {
      setSections(r.data.sections || []);
      setMeta(r.data);
    }).catch(() => toast.error("Failed to load layout"));
  }, [scope, layoutType, planKey, venueId, loading]);

  const labelFor = useMemo(() => {
    const list = layoutType === "public" ? catalogue.public_sections : catalogue.owner_modules;
    const map = {};
    (list || []).forEach((s) => { map[s.key] = s.label; });
    return map;
  }, [catalogue, layoutType]);

  // Map each section key → its plan feature_toggle key, then resolve which
  // toggles are OFF on the currently-edited plan so we can render a "Locked
  // by plan" badge on rows that would be hidden by gating regardless of the
  // layout switch state. Single source of truth: the backend toggle map.
  const toggleMap = useMemo(() => (
    layoutType === "public" ? (catalogue.public_toggle_map || {}) : (catalogue.owner_toggle_map || {})
  ), [catalogue, layoutType]);

  // Pick the active plan: in global scope it's planKey directly; in venue
  // scope we read it from the selected venue's `venue_plan_key`.
  const activePlanKey = useMemo(() => {
    if (scope === "global") return planKey;
    const v = venues.find((x) => x.venue_id === venueId);
    return v?.venue_plan_key || "free_listing";
  }, [scope, planKey, venueId, venues]);

  const activePlanToggles = useMemo(() => {
    const p = plans.find((x) => x.plan_key === activePlanKey);
    return p?.feature_toggles || {};
  }, [plans, activePlanKey]);

  // Linked sub-section pairs: child becomes invisible (and locked) when
  // parent is hidden. Mirrors backend `_apply_plan_gating` so the admin
  // builder visualises the same coupling the public site enforces.
  const LINKED_SECTIONS = layoutType === "public"
    ? { review_submission_form: "reviews_section" }
    : {};

  const lockedBy = (sectionKey) => {
    const tk = toggleMap[sectionKey];
    if (tk && activePlanToggles[tk] === false) return tk;
    // Linked-parent lock — e.g., review_submission_form is forced off when
    // reviews_section is hidden in the same layout.
    const parentKey = LINKED_SECTIONS[sectionKey];
    if (parentKey) {
      const parent = sections.find((s) => s.key === parentKey);
      const parentToggle = toggleMap[parentKey];
      const parentPlanLocked = parentToggle && activePlanToggles[parentToggle] === false;
      if (parent && (parent.visible === false || parentPlanLocked)) {
        return `↑ ${parentKey}`;
      }
    }
    return null;
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((s) => s.key === active.id);
    const newIndex = sections.findIndex((s) => s.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setSections(arrayMove(sections, oldIndex, newIndex));
  };

  const toggleVisible = (key) => {
    setSections(sections.map((s) => s.key === key ? { ...s, visible: !s.visible } : s));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { layout_type: layoutType, plan_key: planKey, sections };
      const url = scope === "venue"
        ? `${API}/admin/venues/${venueId}/layout/${layoutType}`
        : `${API}/admin/layouts/global/${planKey}/${layoutType}`;
      const r = await axios.put(url, payload);
      setSections(r.data.sections || []);
      toast.success(t("admin_layouts:toast_saved", { defaultValue: "Layout saved" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t("admin_layouts:confirm_reset", {
      defaultValue: "Revert to the inherited layout? Your override will be deleted.",
    }))) return;
    try {
      const url = scope === "venue"
        ? `${API}/admin/venues/${venueId}/layout/${layoutType}`
        : `${API}/admin/layouts/global/${planKey}/${layoutType}`;
      await axios.delete(url);
      toast.success(t("admin_layouts:toast_reset", { defaultValue: "Layout reset" }));
      // Force reload via a cheap state poke.
      setScope((s) => s);
      const r = await axios.get(url);
      setSections(r.data.sections || []);
      setMeta(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="admin-layouts-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <LayoutIcon className="h-6 w-6 text-primary" />
          {t("admin_layouts:title", { defaultValue: "Layouts" })}
        </h1>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {t("admin_layouts:intro", {
          defaultValue:
            "Drag sections to reorder. Toggle visibility to show/hide. Per-venue overrides win over the plan default; the plan default wins over the built-in.",
        })}
      </p>

      <div className="bg-card rounded-xl border border-border p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label>{t("admin_layouts:scope", { defaultValue: "Scope" })}</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="mt-1" data-testid="layouts-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="global">{t("admin_layouts:scope_global", { defaultValue: "Global (per plan)" })}</SelectItem>
              <SelectItem value="venue">{t("admin_layouts:scope_venue", { defaultValue: "Per venue" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("admin_layouts:layout_type", { defaultValue: "Type" })}</Label>
          <Select value={layoutType} onValueChange={setLayoutType}>
            <SelectTrigger className="mt-1" data-testid="layouts-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public">{t("admin_layouts:type_public", { defaultValue: "Public profile" })}</SelectItem>
              <SelectItem value="owner">{t("admin_layouts:type_owner", { defaultValue: "Owner dashboard" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {scope === "global" && (
          <div className="md:col-span-2">
            <Label>{t("admin_layouts:plan", { defaultValue: "Plan" })}</Label>
            <Select value={planKey} onValueChange={setPlanKey}>
              <SelectTrigger className="mt-1" data-testid="layouts-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.plan_key} value={p.plan_key}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {scope === "venue" && (
          <div className="md:col-span-2">
            <Label>{t("admin_layouts:venue", { defaultValue: "Venue" })}</Label>
            <Select value={venueId} onValueChange={setVenueId}>
              <SelectTrigger className="mt-1" data-testid="layouts-venue"><SelectValue /></SelectTrigger>
              <SelectContent>
                {venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!loading && sections.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid="layouts-sections">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {scope === "venue" && meta.has_override === false &&
                t("admin_layouts:inherited_hint", {
                  defaultValue: "Currently inheriting from {{src}}. Save to create a venue-level override.",
                  src: meta.resolved_source || "default",
                })}
              {scope === "global" && meta.is_default &&
                t("admin_layouts:using_default_hint", {
                  defaultValue: "Using built-in default. Save to override for this plan.",
                })}
            </span>
            <span>{sections.length} {t("admin_layouts:sections", { defaultValue: "sections" })}</span>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sections.map((s) => s.key)} strategy={verticalListSortingStrategy}>
              {sections.map((s, idx) => (
                <SortableRow
                  key={s.key}
                  id={s.key}
                  index={idx}
                  label={labelFor[s.key] || s.key}
                  visible={s.visible}
                  toggleKey={toggleMap[s.key]}
                  lockedToggle={lockedBy(s.key)}
                  planKey={activePlanKey}
                  onToggle={() => toggleVisible(s.key)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4">
        <Button onClick={handleSave} disabled={saving} data-testid="layouts-save">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {t("admin_layouts:save", { defaultValue: "Save layout" })}
        </Button>
        <Button onClick={handleReset} variant="outline" data-testid="layouts-reset"
          disabled={scope === "venue" ? meta.has_override === false : !!meta.is_default}>
          <RotateCcw className="h-4 w-4 mr-2" />
          {t("admin_layouts:reset", { defaultValue: "Revert to inherited" })}
        </Button>
      </div>
    </div>
  );
}

function SortableRow({ id, index, label, visible, toggleKey, lockedToggle, planKey, onToggle }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isLocked = !!lockedToggle;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isLocked
          ? "bg-amber-50 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800"
          : visible
            ? "bg-background border-border"
            : "bg-muted/40 border-dashed"
      }`}
      data-testid={`layouts-row-${id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        aria-label="Drag handle"
        data-testid={`layouts-drag-${id}`}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="w-6 text-xs text-muted-foreground tabular-nums">{index + 1}</span>
      <span className="flex-1 min-w-0">
        <span className="font-medium text-foreground truncate block">{label}</span>
        {toggleKey && (
          <span className={`text-[10px] font-mono ${isLocked ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>
            {isLocked
              ? t("admin_layouts:locked_by_plan", {
                  defaultValue: "Locked by plan ‘{{plan}}’ — toggle ‘{{toggle}}’ is OFF",
                  plan: planKey,
                  toggle: toggleKey,
                })
              : `${t("admin_layouts:toggle_label", { defaultValue: "controls" })} ${toggleKey}`}
          </span>
        )}
      </span>
      <span className="text-xs text-muted-foreground font-mono shrink-0">{id}</span>
      <Switch
        checked={visible}
        onCheckedChange={onToggle}
        disabled={isLocked}
        data-testid={`layouts-visible-${id}`}
      />
      {isLocked
        ? <EyeOff className="h-4 w-4 text-amber-600" data-testid={`layouts-locked-${id}`} />
        : visible
          ? <Eye className="h-4 w-4 text-emerald-500" />
          : <EyeOff className="h-4 w-4 text-muted-foreground" />}
    </div>
  );
}
